import { defineStore } from "pinia";
import axiosInstance from "@/api/axios";
import { useUserStore } from "@/stores/user";

/**
 * 메뉴 노출 권한 스토어 (Phase B)
 * - 백엔드 GET authz/my-menus/ 로 "내가 볼 수 있는 메뉴 키 목록"을 받아 보관.
 * - App.vue 등에서 menuStore.canSee('design-list') 로 노출 판단.
 * - superuser 는 항상 전부 노출(서버에서도 전부 내려오지만 안전하게 클라에서도 보장).
 */
export const useMenuStore = defineStore("menu", {
  state: () => ({
    keys: [],            // 접근 가능한 메뉴 key 배열
    mobile: {},          // { key: true|false } 모바일 드로어 노출 여부
    titles: {},          // { route: label } 화면 탭 제목 자동 매핑(단일 소스=메뉴)
    isSuperuser: false,
    loaded: false,
  }),
  getters: {
    // canSee('key') -> boolean
    canSee: (state) => (key) => state.isSuperuser || state.keys.includes(key),
    // 경로 -> 메뉴 라벨 (정확 일치 우선, 없으면 최장 접두사 — 상세/하위 경로 커버)
    titleFor: (state) => (path) => {
      if (!path) return ''
      if (state.titles[path]) return state.titles[path]
      let best = ''
      let bestLen = 0
      for (const r in state.titles) {
        if (r && r !== '/' && path.startsWith(r) && r.length > bestLen) {
          best = state.titles[r]; bestLen = r.length
        }
      }
      return best
    },
    // 모바일 드로어 메뉴 노출 여부 (권한 + 모바일 노출 플래그).
    // mobile 맵에 키가 없으면(미로드/구버전 응답) 노출 — 메뉴를 막지 않는 안전 기본값.
    canSeeMobile() {
      return (key) => this.canSee(key) && this.mobile[key] !== false;
    },
  },
  actions: {
    async fetchMyMenus(force = false) {
      // 안전장치: my-menus 호출이 실패해도 superuser는 전체 메뉴를 잃지 않도록 선설정
      const userStore = useUserStore();
      this.isSuperuser = userStore.profile?.is_superuser === true;

      if (this.loaded && !force) return;
      try {
        const res = await axiosInstance.get("authz/my-menus/");
        this.keys = Array.isArray(res.data?.menus) ? res.data.menus : [];
        this.mobile = res.data?.mobile && typeof res.data.mobile === "object"
          ? res.data.mobile : {};
        this.titles = res.data?.titles && typeof res.data.titles === "object"
          ? res.data.titles : {};
        this.isSuperuser = !!res.data?.is_superuser;
        this.loaded = true;
      } catch (err) {
        // 로드 실패 시: 메뉴를 막아버리면 사용성이 크게 깨지므로 loaded=false 로 두어
        // 다음 네비게이션에서 재시도하게 한다. (superuser 는 어차피 전부 노출)
        console.error("my-menus 로드 실패:", err);
        this.loaded = false;
      }
    },
    reset() {
      this.keys = [];
      this.mobile = {};
      this.titles = {};
      this.isSuperuser = false;
      this.loaded = false;
    },
  },
});
